import type {
  ApprovalResponse,
  ChildAgentRunMetadata,
  Message,
  PendingApproval,
  StreamDiagnostics,
  ToolExecutionStatus,
  ToolResultBlock,
  ToolTerminalOutcome,
  ToolUseStatus,
} from '../../../contracts/harness.types';
import type { SeenAtMs } from './identity';

/** Ascending rank: persisted outranks streamed outranks optimistic. */
export type TranscriptSource = 'optimistic' | 'streamed' | 'persisted';

export type LiveToolStatus = Extract<ToolUseStatus, 'running' | 'complete' | 'error'>;

interface TranscriptEntryBase {
  /**
   * Permanent render identity, minted once and never re-minted, so an optimistic
   * bubble upgrades in place rather than remounting. NOT the identity keys that
   * index it (`store.keys`), which an entry collects more of over time.
   */
  id: string;
  source: TranscriptSource;
}

export interface UserTranscriptEntry extends TranscriptEntryBase {
  kind: 'user';
  message: Message;
  turnId?: string;
  iteration?: number;
  createdAt?: string;
  position: number;
  /** Persisted user-message count at mint time; the claim's lower bound. */
  baselineUserCount?: number;
  /** The turn POST reached the backend. Only then may a fetch claim this entry. */
  accepted: boolean;
}

export interface AssistantTranscriptEntry extends TranscriptEntryBase {
  kind: 'assistant';
  message: Message;
  /** Cleared at the turn boundary; only a settled entry is twin-matchable. */
  streaming: boolean;
  artifactId?: string;
  messageIndex?: number;
  turnId?: string;
  iteration?: number;
  createdAt?: string;
  seenAtMs?: SeenAtMs;
  /**
   * Per-block first/last server-frame stamps, index-aligned with
   * `message.blocks`. Only the stream ingestor maintains this; snapshot and
   * optimistic entries omit it.
   */
  blockSeenAtMs?: SeenAtMs[];
}

interface MarkerTranscriptEntryBase extends TranscriptEntryBase {
  kind: 'marker';
  artifactId?: string;
  turnId?: string;
  iteration?: number;
  createdAt?: string;
}

export type TranscriptMarker =
  | { marker: 'turn_cancelled' | 'compaction_marker' }
  | { marker: 'turn_failed'; reason: string | null };

export type MarkerTranscriptEntry = MarkerTranscriptEntryBase & TranscriptMarker;

/** All of a tool call EXCEPT its `tool_call` block, which rides on the assistant
 *  entry that emitted it and is joined by id at fold time. */
export interface ToolTranscriptEntry extends TranscriptEntryBase {
  kind: 'tool';
  toolCallId: string;
  result?: ToolResultBlock;
  /** The backend's first-class outcome, off the tool_execution artifact. */
  persistedStatus?: ToolExecutionStatus;
  liveStatus?: LiveToolStatus;
  liveOutput?: string;
  /** Accumulated tool-input JSON from live `tool_input_delta` events. */
  livePartialInput?: string;
  liveToolName?: string;
  childRun?: ChildAgentRunMetadata;
  pendingApproval?: PendingApproval;
  approval?: ApprovalResponse;
  /** Resolved or its turn ended: kept (nothing is removed) but no longer gating. */
  pendingApprovalStale?: boolean;
  turnId?: string;
  iteration?: number;
  seenAtMs?: SeenAtMs;
  resultCreatedAt?: string;
  terminalOutcome?: ToolTerminalOutcome;
}

export interface PromptContextFailureTranscriptEntry extends TranscriptEntryBase {
  kind: 'prompt_context_failure';
  eventId: string;
  /** Server-provided detail; `null` when the event carried no message. */
  text: string | null;
}

export type TranscriptEntry =
  | UserTranscriptEntry
  | AssistantTranscriptEntry
  | MarkerTranscriptEntry
  | ToolTranscriptEntry
  | PromptContextFailureTranscriptEntry;

export type PositionalTranscriptEntry =
  | UserTranscriptEntry
  | AssistantTranscriptEntry
  | MarkerTranscriptEntry;

/**
 * One chat lifetime's transcript. Ingestion is add-or-upgrade only — no source
 * subtracts, so a fetch that has not caught up cannot make a reply vanish.
 * Rotation replaces the whole store and is the only whole-store clear.
 */
export interface TranscriptStore {
  byId: Readonly<Record<string, TranscriptEntry>>;
  /** Identity key → entry id. One entry is reachable by several keys. */
  keys: Readonly<Record<string, string>>;
  order: readonly string[];
  toolOrder: readonly string[];
  failureOrder: readonly string[];
  nextOrdinal: number;
  nextStreamSlot: number;
  activeAssistantId: string | null;
  /** Highest stream id ingested. The stream is strictly ordered and resumes
   *  forward, so this alone is a complete duplicate guard. */
  lastStreamId: string | null;
  streamDiagnostics: StreamDiagnostics | null;
}

export const EMPTY_TRANSCRIPT_STORE: TranscriptStore = {
  byId: {},
  keys: {},
  order: [],
  toolOrder: [],
  failureOrder: [],
  nextOrdinal: 0,
  nextStreamSlot: 0,
  activeAssistantId: null,
  lastStreamId: null,
  streamDiagnostics: null,
};
